import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from "@angular/core";
import { Plugin } from "@egjs/ngx-flicking";
import { Arrow, AutoPlay } from "@egjs/flicking-plugins";
import { Router } from "@angular/router";
import { combineLatest } from "rxjs";
import { take } from "rxjs/operators";
import { Compbaser } from "../../../components/compbaser";
import { LocalStoreService, StoreSelectorsService } from "../../../services";
import { convertBooleanToNumbers, convertNumberToBoolean, PREVIOUSLY_USED_ACCOUNT } from "../../../common";
import { faCircleChevronLeft, faCircleChevronRight } from "@fortawesome/free-solid-svg-icons";
import { TouchLib } from "../touch/touchLib";
import { SceneMimeTypes } from "../scene-blocks/mime-constants";

interface IRecentItem {
    sceneId: string;
    name: string;
    width: number;
    height: number;
    loaded: boolean;
}

@Component({
    selector: 'app-scene-recents',
    templateUrl: './scene-recents.component.html',
    styleUrls: ['./scene-recents.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SceneRecentsComponent extends Compbaser implements OnInit {
    readonly faArrowLeft = faCircleChevronLeft;
    readonly faArrowRight = faCircleChevronRight;

    accountInitialCopyResources;
    PREVIOUSLY_USED_ACCOUNT = PREVIOUSLY_USED_ACCOUNT;

    plugins: Plugin[] = [
        new Arrow({ moveByViewportSize: false, moveCount: 3 }),
        new AutoPlay({ duration: 10000, direction: "NEXT", stopOnHover: true })
    ];

    options = {
        gap: 10,
        circular: true,
        align: 'prev',
        horizontal: true,
        // moveType: 'freeScroll'
    };

    recentScenes: IRecentItem[] = [];
    scenePreviewEnabled = true;

    @Input() vertical: boolean = false;
    @Input() count: number = 10;

    constructor(
        private storeSelector: StoreSelectorsService,
        private cd: ChangeDetectorRef,
        private localStoreService: LocalStoreService,
        private router: Router,
    ) {
        super();
    }

    ngOnInit(): void {
        if (this.vertical) {
            this.options.horizontal = false;
        }
        this.scenePreviewEnabled = convertNumberToBoolean(this.localStoreService.getStorageValConfig('recentScenePreviewEnabled'));
        
        this.cod(
            this.storeSelector.getLocalStoreUpdates()
                .subscribe((updates) => {
                    if (!updates) return;
                    this.accountInitialCopyResources = this.localStoreService.getStorageValConfig('accountInitialCopyResources');
                    this.cd.detectChanges();
                })
        );
        this.cod(
            this.storeSelector.getScenes().subscribe((v) => {
                if (v && v.length > 0) {
                    const sortedByModified = [...v].sort((a, b) => {
                        if (b.Player.$.lastModified) {
                            if (a.Player.$.lastModified && b.Player.$.lastModified) {
                                return parseInt(b.Player.$.lastModified) - parseInt(a.Player.$.lastModified);
                            }
                            return 1;
                        }
                        return -1;
                    });

                    this.recentScenes = sortedByModified.slice(0, this.count).map((_scene) => ({
                        sceneId: this.getPreviewSceneId(_scene),
                        name: _scene.Player.$.label,
                        width: parseInt(_scene.Player?.Data[0]?.Layout[0]?.$.width),
                        height: parseInt(_scene.Player?.Data[0]?.Layout[0]?.$.height),
                        loaded: this.scenePreviewEnabled ? false: true
                    }));
                    this.cd.detectChanges();
                }
            })
        );
    }

    jumpToSceneItem(sceneId) {
        this.cod(
            combineLatest([
                this.storeSelector.getScene(sceneId),
                this.storeSelector.getScenes()
            ]).pipe(take(1)).subscribe(([scene, allScenes]) => {
                if (scene?.Player?.$.mimeType === SceneMimeTypes.Touch) {
                    let targetSceneId = sceneId;

                    // If this is a child scene (private_child), find the parent wrapper scene
                    const privateChild = scene.Player.$.private_child;
                    const isPrivateChild = (privateChild as any) === 'true' || privateChild === true || (privateChild as any) === 'True';

                    if (isPrivateChild) {
                        const parentScene = TouchLib.findParentTouchScene(sceneId, allScenes);
                        if (parentScene) {
                            targetSceneId = parentScene.Player.$.id;
                        }
                    }

                    this.router.navigateByUrl(`/editor/touch/scene/${targetSceneId}`);
                } else {
                    this.router.navigateByUrl(`/editor/list/${sceneId}`);
                }
            })
        );
    }

    onSceneLoaded(sceneId) {
        setTimeout(() => {
            const s = this.recentScenes.find((r) => r.sceneId == sceneId)
            if (s) {
                s.loaded = true;
                this.cd.detectChanges();
            }
        }, 2000);
    }

    toggleScenePreview2() {
        this.scenePreviewEnabled = !this.scenePreviewEnabled;
        const val = convertBooleanToNumbers(this.scenePreviewEnabled, true);
        this.localStoreService.setStorageValConfig('recentScenePreviewEnabled', val);
    }

    /**
     * If scene is touch mimetype, get first child slide scene id for preview
     * Else, return scene id
     * @param scene
     * @returns
     */
    getPreviewSceneId(scene) {
        if (scene.Player.$.mimeType === SceneMimeTypes.Touch) {
            return TouchLib.getFirstSlideSceneId(scene);
        }
        return scene.Player.$.id;
    }
}
